import { v4 as uuidv4 } from 'uuid';
import { calendarService } from './calendarService';
import { emailService } from './emailService';

const BOOKINGS_KEY = 'appointment_app_bookings';

class BookingService {
  constructor() {
    this.initializeStorage();
  }

  initializeStorage() {
    if (!localStorage.getItem(BOOKINGS_KEY)) {
      localStorage.setItem(BOOKINGS_KEY, JSON.stringify([]));
    }
  }

  async createBooking(shareId, slotId, bookerInfo) {
    try {
      const calendar = await calendarService.getCalendarByShareId(shareId);
      const slot = calendar.slots.find(s => s.id === slotId);
      
      if (!slot || !slot.isAvailable) {
        throw new Error('Zeitslot nicht verfügbar');
      }

      const bookings = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
      
      // Check if slot is already fully booked
      const existingBookings = bookings.filter(b => b.slotId === slotId && b.status === 'confirmed');
      if (existingBookings.length >= calendar.settings.maxBookingsPerSlot) {
        throw new Error('Zeitslot bereits ausgebucht');
      }

      const booking = {
        id: uuidv4(),
        calendarId: calendar.id,
        slotId: slotId,
        shareId: shareId,
        booker: {
          name: bookerInfo.name,
          email: bookerInfo.email,
          phone: bookerInfo.phone || '',
          notes: bookerInfo.notes || ''
        },
        status: calendar.settings.requireApproval ? 'pending' : 'confirmed',
        createdAt: new Date().toISOString(),
        confirmedAt: calendar.settings.requireApproval ? null : new Date().toISOString()
      };

      bookings.push(booking);
      localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings));

      // Send confirmation email
      await emailService.sendBookingConfirmation(booking, slot, calendar);

      return booking;
    } catch (error) {
      throw error;
    }
  }

  async getUserBookings() {
    // This would typically get bookings for calendars owned by the user
    // For now, return all bookings (in a real app, filter by calendar ownership)
    const bookings = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
    return bookings;
  }

  async getBookingById(id) {
    const bookings = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
    const booking = bookings.find(b => b.id === id);
    
    if (!booking) throw new Error('Buchung nicht gefunden');
    return booking;
  }

  async cancelBooking(id, reason = '') {
    const bookings = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
    const bookingIndex = bookings.findIndex(b => b.id === id);
    
    if (bookingIndex === -1) throw new Error('Buchung nicht gefunden');

    bookings[bookingIndex] = {
      ...bookings[bookingIndex],
      status: 'cancelled',
      cancelledAt: new Date().toISOString(),
      cancellationReason: reason
    };

    localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings));

    // Send cancellation email
    await emailService.sendBookingCancellation(bookings[bookingIndex]);

    return bookings[bookingIndex];
  }

  async confirmBooking(id) {
    const bookings = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
    const bookingIndex = bookings.findIndex(b => b.id === id);
    
    if (bookingIndex === -1) throw new Error('Buchung nicht gefunden');

    bookings[bookingIndex] = {
      ...bookings[bookingIndex],
      status: 'confirmed',
      confirmedAt: new Date().toISOString()
    };

    localStorage.setItem(BOOKINGS_KEY, JSON.stringify(bookings));
    return bookings[bookingIndex];
  }

  async getCalendarBookings(calendarId) {
    const bookings = JSON.parse(localStorage.getItem(BOOKINGS_KEY));
    return bookings.filter(b => b.calendarId === calendarId);
  }
}

export const bookingService = new BookingService();